import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Helmet } from 'react-helmet-async';
import { Button } from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { PageHeader } from 'components/page-header';
import { pageTitle } from 'utils/page-title';
import { promptsAtom, selectionAtom, loadableErrorMessage } from './atoms';
import { FilterPanelContent } from './filter-panel-content';
import { VersionLogSubrow } from './version-log-subrow';
import { ViewPromptPanel } from './view-prompt-panel';
import type { PromptGroup, PromptVersion } from './types';
import { date } from './format';
import styles from './page.module.scss';

export function PromptManagementPage() {
  const promptsState = useAtomValue(promptsAtom);
  const loading = promptsState.state === 'loading';
  const error = loadableErrorMessage(promptsState, 'Failed to load prompts');

  const groups = useMemo<PromptGroup[]>(
    () => (promptsState.state === 'hasData' ? promptsState.data.prompts : []),
    [promptsState]
  );

  const documentTypeOptions = useMemo(
    () =>
      groups
        .filter((g) => g.kind === 'documentType')
        .map((g) => ({ key: g.key, label: g.label })),
    [groups]
  );
  const agentOptions = useMemo(
    () =>
      groups
        .filter((g) => g.kind === 'agent')
        .map((g) => ({ key: g.key, label: g.label })),
    [groups]
  );

  const setSelection = useSetAtom(selectionAtom);

  const openVersion = useCallback(
    (group: PromptGroup, version: PromptVersion) => {
      setSelection({
        ref: version.id,
        title: `${group.label} ${version.version}`,
      });
    },
    [setSelection]
  );
  const openActive = useCallback(
    (group: PromptGroup) => {
      setSelection({
        ref: group.activeVersionId,
        title: `${group.label} ${group.activeVersion}`,
      });
    },
    [setSelection]
  );

  const columns = useMemo<DataTableColumnDef<PromptGroup>[]>(
    () => [
      // The left-panel checkboxes drive this column's filter (matched on the
      // group key) so the table's built-in Reset governs the selection. Kept on
      // the visible Prompt column so no hidden column skews width distribution.
      {
        id: 'label',
        accessorKey: 'label',
        header: 'Prompt',
        size: 220,
        meta: { type: 'text' },
        filterFn: (
          row: Row<PromptGroup>,
          _columnId: string,
          filterValue: string[]
        ) =>
          !filterValue ||
          filterValue.length === 0 ||
          filterValue.includes(row.original.key),
      },
      {
        id: 'category',
        accessorFn: (row: PromptGroup) => {
          return row.kind === 'agent' ? 'Agent' : 'Document';
        },
        header: 'Category',
        size: 120,
        meta: { type: 'text' },
      },
      {
        id: 'activeVersion',
        accessorKey: 'activeVersion',
        header: 'Active Version',
        size: 140,
        meta: { type: 'text' },
      },
      {
        id: 'createdBy',
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 150,
        meta: { type: 'text' },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 140,
        cell: ({ row }) => date(row.original.createdAt),
      },
      {
        id: 'updatedBy',
        accessorKey: 'updatedBy',
        header: 'Updated By',
        size: 150,
        meta: { type: 'text' },
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: 'Updated At',
        size: 140,
        cell: ({ row }) => date(row.original.updatedAt),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            kind="ghost"
            size="sm"
            onClick={() => openActive(row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    [openActive]
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle('Prompt Management')}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[]}
        title="Prompt Management"
        actions={null}
        compactContent
      />
      <div className={styles.contentWrapper}>
        <DataTable<PromptGroup>
          data={groups}
          columns={columns as ColumnDef<PromptGroup>[]}
          getRowId={(row) => row.key}
          isLoading={loading}
          error={error}
          searchPlaceholder="Search by document type or agent"
          scroll={{ mode: 'paginated' }}
          initialTableState={{ sortColumn: 'label', sortDirection: 'asc' }}
          config={{
            globalSearch: true,
            columnFilters: false,
            sorting: true,
            columnVisibility: true,
          }}
          renderFilterPanel={({ columnFilters, setColumnFilters }) => (
            <FilterPanelContent
              documentTypes={documentTypeOptions}
              agents={agentOptions}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
            />
          )}
          getRowCanExpand={() => true}
          renderRowSubComponent={(row) => (
            <VersionLogSubrow
              group={row.original}
              onViewVersion={(version) => openVersion(row.original, version)}
            />
          )}
        />
      </div>
      <ViewPromptPanel />
    </>
  );
}
